<script lang="ts">
  import { fade, slide } from 'svelte/transition';
  import { errorService } from '$lib/services/ErrorService';
  import type { ErrorSeverity } from '$lib/hooks/useError';
  
  // Get errors from service
  const errors = errorService.errors;
  const activeError = errorService.activeError;
  
  // Icon mappings
  const severityIcons: Record<ErrorSeverity, string> = {
    info: 'ℹ️',
    warning: '⚠️',
    error: '❌',
    critical: '🚨'
  };
  
  // Color classes
  const severityClasses: Record<ErrorSeverity, string> = {
    info: 'notification-info',
    warning: 'notification-warning',
    error: 'notification-error',
    critical: 'notification-critical'
  };
  
  function dismissError(id: string) {
    errorService.dismissError(id);
  }
  
  function dismissAll() {
    errorService.clearAllErrors();
  }
</script>

{#if $activeError}
  <div class="error-notification-container">
    <div
      class="error-notification {severityClasses[$activeError.severity]}"
      transition:slide={{ duration: 300 }}
    >
      <div class="notification-content">
        <span class="notification-icon">
          {severityIcons[$activeError.severity]}
        </span>
        <span class="notification-message">
          {$activeError.message}
        </span>
        {#if $activeError.context?.component}
          <span class="notification-source">
            [{$activeError.context.component}]
          </span>
        {/if}
      </div>
      
      <div class="notification-actions">
        {#if $errors.length > 1}
          <span class="error-count">
            {$errors.length} errors
          </span>
        {/if}
        
        <button
          class="dismiss-button"
          onclick={() => dismissError($activeError.id)}
          aria-label="Dismiss error"
        >
          ✕
        </button>
      </div>
    </div>
    
    {#if $errors.length > 1}
      <button
        class="dismiss-all-button"
        onclick={dismissAll}
        transition:fade={{ duration: 200 }}
      >
        Clear all errors
      </button>
    {/if}
  </div>
{/if}

<style>
  .error-notification-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    max-width: 400px;
    width: calc(100vw - 40px);
  }
  
  .error-notification {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 1.5;
    animation: slideIn 0.3s ease-out;
  }
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  .notification-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }
  
  .notification-icon {
    flex-shrink: 0;
    font-size: 16px;
  }
  
  .notification-message {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .notification-source {
    font-size: 12px;
    opacity: 0.7;
    flex-shrink: 0;
  }
  
  .notification-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  
  .error-count {
    font-size: 12px;
    opacity: 0.7;
    padding: 2px 6px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
  
  .dismiss-button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 4px;
    opacity: 0.7;
    transition: opacity 0.2s;
    font-size: 16px;
    line-height: 1;
  }
  
  .dismiss-button:hover {
    opacity: 1;
  }
  
  .dismiss-all-button {
    width: 100%;
    padding: 8px;
    background: rgba(0, 0, 0, 0.1);
    border: none;
    border-radius: 4px;
    color: inherit;
    cursor: pointer;
    font-size: 12px;
    transition: background 0.2s;
  }
  
  .dismiss-all-button:hover {
    background: rgba(0, 0, 0, 0.2);
  }
  
  /* Severity styles */
  .notification-info {
    background: #e3f2fd;
    color: #1565c0;
    border-left: 4px solid #1976d2;
  }
  
  .notification-warning {
    background: #fff3e0;
    color: #e65100;
    border-left: 4px solid #f57c00;
  }
  
  .notification-error {
    background: #ffebee;
    color: #c62828;
    border-left: 4px solid #d32f2f;
  }
  
  .notification-critical {
    background: #d32f2f;
    color: white;
    border-left: 4px solid #b71c1c;
    animation: pulse 1s infinite;
  }
  
  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 4px 12px rgba(211, 47, 47, 0.4);
    }
    50% {
      box-shadow: 0 4px 20px rgba(211, 47, 47, 0.6);
    }
  }
  
  /* Dark mode support */
  :global(:root.dark-theme) .notification-info {
    background: #0d47a1;
    color: #bbdefb;
  }
  
  :global(:root.dark-theme) .notification-warning {
    background: #e65100;
    color: #ffe0b2;
  }
  
  :global(:root.dark-theme) .notification-error {
    background: #c62828;
    color: #ffcdd2;
  }
  
  :global(:root.dark-theme) .notification-critical {
    background: #b71c1c;
    color: white;
  }
  
  /* Mobile responsive */
  @media (max-width: 480px) {
    .error-notification-container {
      top: 10px;
      right: 10px;
      left: 10px;
      width: auto;
      max-width: none;
    }
    
    .error-notification {
      font-size: 13px;
    }
  }
</style>